import React, {useEffect, useRef, useState} from 'react';
import Dialog from "@material-ui/core/Dialog";
import {DialogContent, FormControlLabel, LinearProgress, RadioGroup} from "@material-ui/core";
import {Trans} from "react-i18next";

import './style.less';
import {StyledRadio} from '../../../../../../../components/RadioButtons';
import applyFixedDatesIcon from '../../../../../../../../../../../../../components/ActionDialog/images/availability_updated.svg';
import finishedFixedDatesIcon from '../../../../../../../../../../../../../components/ActionDialog/images/success.svg';
import {useAuth} from "../../../../../../../../../../../../../services/authServices.js";
import {propertyServices} from "../../../../../../../../../../../../../services/index.js";

const formatDayMonth = ({day, month}) => String(day).padStart(2, "0") + "/" + String(month).padStart(2, "0");

//The progress bar moves by an estimate of how long each property takes: the first is assumed to take this long, the
//next ones the average of the properties already updated
const FIRST_PROPERTY_ESTIMATED_MS = 2000;
//share of a property's step the bar can fill before that property is done, so it never runs into the next step
const MAX_STEP_SHARE_BEFORE_DONE = 0.95;
//lets the bar visibly reach the end before the finished screen
const FINISHED_DELAY_MS = 250;

const initialProgress = () => ({completed: 0, stepStart: Date.now(), estimate: FIRST_PROPERTY_ESTIMATED_MS, durations: []});

//Same flow as the booking window "apply to all properties" dialog (LandlordRules5): copies the fixed dates being
//edited to the other rooms of this listing or to every listing of the landlord, one property at a time.
//The room being edited is left out, it is saved by the RoomPrices form itself.
const ApplyFixedDatesModal = ({open, onClose, onApplied, t, pageLabel, property, currentRoomName, availabilityPeriods}) => {
    const {user} = useAuth();

    const hasOtherRooms = Object.keys(property.rooms || {}).some(room_id => room_id !== currentRoomName);
    const [scope, setScope] = useState(null);
    const [updatingProperties, setUpdatingProperties] = useState(0);
    const [currentUpdatingProperty, setCurrentUpdatingProperty] = useState(0);
    const [finished, setFinished] = useState(false);
    const [failedProperties, setFailedProperties] = useState(0);
    const progressRef = useRef(initialProgress());
    const [progress, setProgress] = useState(0);

    const isUpdating = updatingProperties > 0 && !finished;

    useEffect(() => {
        if (!open) return;
        setScope(hasOtherRooms ? null : "all");
        setUpdatingProperties(0);
        setCurrentUpdatingProperty(0);
        setFinished(false);
        setFailedProperties(0);
        progressRef.current = initialProgress();
        setProgress(0);
    }, [open]);

    useEffect(() => {
        if (!isUpdating) return;
        let frame;
        const tick = () => {
            const {completed, stepStart, estimate} = progressRef.current;
            const stepShare = completed < updatingProperties ?
                Math.min((Date.now() - stepStart) / estimate, MAX_STEP_SHARE_BEFORE_DONE) : 0;
            setProgress((completed + stepShare) / updatingProperties * 100);
            frame = requestAnimationFrame(tick);
        };
        tick();
        return () => cancelAnimationFrame(frame);
    }, [isUpdating, updatingProperties]);

    const handleApply = async () => {
        const propertyIds = scope === "all" ?
            [property.id, ...(user?.houses || []).filter(property_id => property_id !== property.id)] :
            [property.id];
        progressRef.current = initialProgress();
        setUpdatingProperties(propertyIds.length);
        let failed = 0;
        for (let i = 0; i < propertyIds.length; i++) {
            setCurrentUpdatingProperty(i);
            const isCurrentProperty = propertyIds[i] === property.id;
            try {
                const {rooms} = await propertyServices.applyFixedDates(propertyIds[i], availabilityPeriods, isCurrentProperty ? currentRoomName : undefined);
                if (isCurrentProperty) onApplied(rooms);
            } catch (e) {
                console.error("Error applying fixed dates to property", propertyIds[i], e);
                failed++;
            }
            const progressState = progressRef.current;
            progressState.durations.push(Date.now() - progressState.stepStart);
            progressState.estimate = progressState.durations.reduce((sum, duration) => sum + duration, 0) / progressState.durations.length;
            progressState.completed = i + 1;
            progressState.stepStart = Date.now();
        }
        await new Promise(resolve => setTimeout(resolve, FINISHED_DELAY_MS));
        setFailedProperties(failed);
        setFinished(true);
    };

    const periodsList = <ul className={"periods-list"}>
        {availabilityPeriods.map((period, index) => <li key={index}>
            {t(pageLabel + ".availabilitySlots.fixedMoveIn")}: <strong>{formatDayMonth(period.move_in.start)} - {formatDayMonth(period.move_in.end)}</strong>
            <br/>
            {t(pageLabel + ".availabilitySlots.fixedMoveOut")}: <strong>{formatDayMonth(period.move_out.start)} - {formatDayMonth(period.move_out.end)}</strong>
        </li>)}
    </ul>;

    return <Dialog open={open} onClose={() => !isUpdating && onClose()} className={`ApplyFixedDatesModal ActionDialog`} maxWidth={"xs"}>
        {finished ? <DialogContent className={"ActionDialog__content"}>
            <img src={finishedFixedDatesIcon}/>
            <div className={"title"}>{t(pageLabel + ".applyFixedDates.finished.title")}</div>
            <small className={"small-desc"}>
                {t(pageLabel + ".applyFixedDates.finished." + (scope === "all" ? "desc_all" : "desc_property"))}
            </small>
            {failedProperties > 0 && <small className={"small-desc error"}>
                {t(pageLabel + ".applyFixedDates.finished.failed", {count: failedProperties})}
            </small>}
            <small className={"small-desc"}>{t(pageLabel + ".applyFixedDates.finished.save_current")}</small>
            <div className={"buttons_container"}>
                <button type={"button"} className={"btn btn-primary"} onClick={onClose}>{t('buttons.continue')}</button>
            </div>
        </DialogContent> : (isUpdating ? <DialogContent className={"ActionDialog__content"}>
            <div className={"title"}>{t(pageLabel + ".applyFixedDates.updating")}</div>
            <h1>{(currentUpdatingProperty + 1)} / {updatingProperties}</h1>
            <LinearProgress className={"progress-bar"} variant={"determinate"}
                            value={progress}/>
        </DialogContent> : <DialogContent className={"ActionDialog__content"}>
            <img src={applyFixedDatesIcon}/>
            <div className={"title"}>{t(pageLabel + ".applyFixedDates.modal.title")}</div>
            <small className={"small-desc"}>
                <Trans i18nKey={pageLabel + ".applyFixedDates.modal.desc"}
                       components={{blue: <span style={{color: "#57B4F2"}}/>}}/>
            </small>
            {periodsList}
            <div className={"apply-input-radio"}>
                <strong className={"apply-input-radio_label"}>{t(pageLabel + ".applyFixedDates.modal.label")}</strong>
                <div className={"RadioButtons"}>
                    <RadioGroup value={scope || ""} onChange={(e) => setScope(e.target.value)}>
                        {hasOtherRooms && <FormControlLabel value="property" control={<StyledRadio/>}
                                                            label={t(pageLabel + ".applyFixedDates.modal.option_property")}/>}
                        <FormControlLabel value="all" control={<StyledRadio/>}
                                          label={t(pageLabel + ".applyFixedDates.modal.option_all")}/>
                    </RadioGroup>
                </div>
            </div>

            <div className={"buttons_container"}>
                <button type={"button"} className={"btn btn-secondary"} onClick={onClose}>{t('buttons.cancel')}</button>
                <button type={"button"} className={"btn btn-primary"} disabled={scope == null}
                        onClick={handleApply}>{t('buttons.confirm')}</button>
            </div>
        </DialogContent>)}
    </Dialog>
};

export default ApplyFixedDatesModal;
